import React, { useState, useEffect, useCallback, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import ViewerNavbar from "./ViewerNavbar";
import API from "../../services/api.ts";
import { getMediaUrl } from "../../services/media.ts";
import "./DocumentViewerPage.css";

// Filename-prefix -> category. First match wins. Users can rename their files
// (Rad_..., Path_..., Lab_...) to influence grouping until we add an explicit
// document_category metadata field.
const CATEGORY_RULES = [
    { key: "Radiology", pattern: /^rad[_-]|radiolog/i },
    { key: "Pathology", pattern: /^path[_-]|patholog/i },
    { key: "Labs", pattern: /^lab[_-]|labs?[_-]|blood[_-]/i },
    { key: "Procedures", pattern: /^op[_-]|surg|procedure|operative/i },
    { key: "Admissions", pattern: /^adm[_-]|admission|discharge/i },
    { key: "Clinical Notes", pattern: /^note|clinical|history|hpi|consult/i },
];

const inferCategory = (filename) => {
    if (!filename) return "Other";
    for (const rule of CATEGORY_RULES) {
        if (rule.pattern.test(filename)) return rule.key;
    }
    return "Other";
};

const isPdf = (docType) => (docType || "").toLowerCase() === "pdf";
const isText = (docType) => ["txt", "md", "text", "markdown"].includes((docType || "").toLowerCase());

const iconForType = (docType) => {
    const t = (docType || "").toLowerCase();
    if (t === "pdf") return "fa-file-pdf-o";
    if (t === "doc" || t === "docx") return "fa-file-word-o";
    if (isText(t)) return "fa-file-text-o";
    return "fa-file-o";
};

const LAYOUT_OPTIONS = [
    { value: 1, label: "1 panel", icon: "fa-square-o" },
    { value: 2, label: "2 panels", icon: "fa-pause" },
    { value: 3, label: "3 panels", icon: "fa-bars" },
    { value: 4, label: "4 panels", icon: "fa-th-large" },
];

const DocumentViewerPage = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const studyIdParam = urlParams.get("study_id");
    const patientIdParam = urlParams.get("patient_id");
    const patientIdsParam = urlParams.get("patient_ids");
    const documentDataParam = urlParams.get("document_data");
    const patientDataStorageKey = urlParams.get("patient_data_storage_key");

    const [studyId] = useState(studyIdParam || localStorage.getItem("studyId"));
    const [patientId, setPatientId] = useState(patientIdParam || "Unknown");
    const [patientIds] = useState(() => {
        if (patientIdsParam) return patientIdsParam.split(",").filter(Boolean);
        return patientIdParam ? [patientIdParam] : [];
    });
    const [currentPatientIndex, setCurrentPatientIndex] = useState(() => {
        if (patientIdsParam && patientIdParam) {
            const ids = patientIdsParam.split(",").filter(Boolean);
            const idx = ids.indexOf(patientIdParam);
            return idx >= 0 ? idx : 0;
        }
        return 0;
    });

    // Study metadata for ViewerNavbar
    const [studyName, setStudyName] = useState("");
    const [labels, setLabels] = useState([]);
    const [labelDisplayMode, setLabelDisplayMode] = useState("dropdown");
    const [questions, setQuestions] = useState([]);
    const [scores, setScores] = useState([]);
    const [shareAnnotations, setShareAnnotations] = useState(false);
    const [viewerNavbarConfig, setViewerNavbarConfig] = useState({});

    // Documents currently loaded for the active patient.
    // Shape per doc: { id, source, filename, document_type, filePath, content }
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);

    // Per-panel state: array of doc IDs (null = empty). Length always matches layout.
    const [layout, setLayout] = useState(1);
    const [panels, setPanels] = useState([null]);
    const [dragOverPanel, setDragOverPanel] = useState(null);

    // Fetch study metadata (labels, questions, etc.)
    const fetchStudyInfo = useCallback(async () => {
        if (!studyId) return;
        try {
            const response = await API.get(`/studies/${studyId}/`);
            setStudyName(response.data.name || "");
            setLabels(response.data.labels || []);
            setLabelDisplayMode(response.data.label_display_mode || "dropdown");
            setQuestions(response.data.questions || []);
            setScores(response.data.scores || []);
            setShareAnnotations(response.data.share_annotations || false);
            setViewerNavbarConfig(response.data.viewer_navbar_config || {});
        } catch (err) {
            console.error("Error fetching study info:", err);
        }
    }, [studyId]);

    // Fetch the documents referenced by document_data, resolving both
    // MedicalDocument and PatientData ids. Text/markdown content is fetched inline
    // so the ReactMarkdown renderer has it ready.
    const fetchDocuments = useCallback(async (docParam) => {
        if (!docParam) {
            setDocuments([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        const items = docParam
            .split(",")
            .map((token) => {
                const [id, source] = token.split(":");
                return { id: (id || "").trim(), source: (source || "MedicalDocument").trim() };
            })
            .filter((item) => item.id);

        const results = [];
        for (const item of items) {
            try {
                let data;
                let contentEndpoint;
                if (item.source === "PatientData") {
                    const r = await API.get(`/data_manager/patient-data/${item.id}/`);
                    data = r.data;
                    data.document_type = data.original_file_type || data.document_type || "txt";
                    data.file = data.file || data.location;
                    contentEndpoint = `/data_manager/patient-data/${item.id}/content/`;
                } else {
                    const r = await API.get(`/data_manager/medical-documents/${item.id}/`);
                    data = r.data;
                    contentEndpoint = `/data_manager/medical-documents/${item.id}/content/`;
                }

                let content = "";
                if (isText(data.document_type)) {
                    try {
                        const c = await API.get(contentEndpoint);
                        content = c.data?.content || "";
                    } catch (err) {
                        content = "Unable to load document content.";
                    }
                }

                results.push({
                    id: `${item.source}-${item.id}`,
                    rawId: item.id,
                    source: item.source,
                    filename: data.filename || `Document ${item.id}`,
                    document_type: data.document_type,
                    filePath: data.file,
                    content,
                    category: inferCategory(data.filename),
                });
            } catch (err) {
                console.error(`Error fetching document ${item.id}:`, err);
            }
        }

        setDocuments(results);
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchStudyInfo();
    }, [fetchStudyInfo]);

    useEffect(() => {
        fetchDocuments(documentDataParam);
    }, [documentDataParam, fetchDocuments]);

    // Pre-fill panels with the first N documents whenever the layout or the doc
    // set changes but the user hasn't manually placed anything yet.
    useEffect(() => {
        setPanels((prev) => {
            const next = new Array(layout).fill(null);
            // Preserve any existing valid placements
            for (let i = 0; i < layout; i++) {
                if (prev[i] && documents.some((d) => d.id === prev[i])) {
                    next[i] = prev[i];
                }
            }
            // Auto-fill remaining empty panels with the next unassigned docs
            const assigned = new Set(next.filter(Boolean));
            for (let i = 0; i < layout; i++) {
                if (next[i]) continue;
                const doc = documents.find((d) => !assigned.has(d.id));
                if (!doc) break;
                next[i] = doc.id;
                assigned.add(doc.id);
            }
            return next;
        });
    }, [layout, documents]);

    // Multi-patient navigation: swap patient_id and refetch that patient's docs
    // from sessionStorage (populated by StudyDashboard when patient_data_storage_key is set).
    const handlePatientChange = useCallback(
        (newIndex) => {
            if (!patientIds.length || newIndex < 0 || newIndex >= patientIds.length) return;
            const newPatientId = patientIds[newIndex];
            setCurrentPatientIndex(newIndex);
            setPatientId(newPatientId);

            if (!patientDataStorageKey) return;
            try {
                const raw = sessionStorage.getItem(patientDataStorageKey);
                if (!raw) return;
                const map = JSON.parse(raw);
                const docs = map[newPatientId]?.document || [];
                fetchDocuments(docs.join(","));
                setPanels(new Array(layout).fill(null));
            } catch (err) {
                console.error("Error switching patient:", err);
            }
        },
        [patientIds, patientDataStorageKey, layout, fetchDocuments]
    );

    // Documents grouped by inferred category, preserving fetch order within each group.
    const grouped = useMemo(() => {
        const g = {};
        documents.forEach((d) => {
            if (!g[d.category]) g[d.category] = [];
            g[d.category].push(d);
        });
        // Return as ordered array so "Other" is always last
        const order = [...CATEGORY_RULES.map((r) => r.key), "Other"];
        return order.filter((k) => g[k]?.length).map((k) => ({ category: k, items: g[k] }));
    }, [documents]);

    const docsById = useMemo(() => {
        const m = {};
        documents.forEach((d) => {
            m[d.id] = d;
        });
        return m;
    }, [documents]);

    // Drag & drop between sidebar / panels
    const handleDragStart = (e, docId) => {
        e.dataTransfer.setData("text/plain", docId);
        e.dataTransfer.effectAllowed = "copyMove";
    };

    const handleDragOver = (e, panelIndex) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setDragOverPanel(panelIndex);
    };

    const handleDragLeave = () => setDragOverPanel(null);

    const handleDrop = (e, panelIndex) => {
        e.preventDefault();
        setDragOverPanel(null);
        const docId = e.dataTransfer.getData("text/plain");
        if (!docId || !docsById[docId]) return;
        setPanels((prev) => {
            const next = [...prev];
            // If the doc is already in another panel, swap so no panel becomes empty
            const sourceIdx = next.indexOf(docId);
            if (sourceIdx !== -1 && sourceIdx !== panelIndex) {
                next[sourceIdx] = next[panelIndex];
            }
            next[panelIndex] = docId;
            return next;
        });
    };

    const handlePanelClose = (panelIndex) => {
        setPanels((prev) => {
            const next = [...prev];
            next[panelIndex] = null;
            return next;
        });
    };

    const handleSidebarClick = (docId) => {
        // Click opens in the first empty panel, or panel 0 if all full.
        setPanels((prev) => {
            const next = [...prev];
            const emptyIdx = next.indexOf(null);
            const target = emptyIdx !== -1 ? emptyIdx : 0;
            const existingIdx = next.indexOf(docId);
            if (existingIdx !== -1 && existingIdx !== target) {
                next[existingIdx] = next[target];
            }
            next[target] = docId;
            return next;
        });
    };

    const availableDataTypes = documents.length > 0 ? ["document"] : [];
    const availableSeries = documents.map((d) => ({
        id: d.id,
        name: d.filename,
        dataType: "document",
    }));

    return (
        <div className="doc-viewer-page">
            <ViewerNavbar
                studyId={studyId}
                studyName={studyName}
                labels={labels}
                labelDisplayMode={labelDisplayMode}
                questions={questions}
                scores={scores}
                patientId={patientId}
                shareAnnotations={shareAnnotations}
                availableDataTypes={availableDataTypes}
                availableSeries={availableSeries}
                patientIds={patientIds}
                currentPatientIndex={currentPatientIndex}
                onPatientChange={handlePatientChange}
                buttonConfig={viewerNavbarConfig}
            />

            <div className="doc-viewer-toolbar">
                <div className="doc-viewer-patient">
                    <i className="fa fa-user-circle" />
                    <div className="doc-viewer-patient-labels">
                        <span className="doc-viewer-patient-caption">Patient ID</span>
                        <span className="doc-viewer-patient-value">{patientId}</span>
                    </div>
                </div>
                <div className="doc-viewer-layout-picker">
                    <span className="doc-viewer-layout-caption">Layout</span>
                    {LAYOUT_OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            className={`doc-viewer-layout-btn ${layout === opt.value ? "active" : ""}`}
                            title={opt.label}
                            onClick={() => setLayout(opt.value)}
                        >
                            <i className={`fa ${opt.icon}`} />
                            <span>{opt.value}</span>
                        </button>
                    ))}
                </div>
                <div className="doc-viewer-name">
                    <i className="fa fa-file-text" />
                    <span>Document Viewer</span>
                </div>
            </div>

            <div className="doc-viewer-body">
                <aside className="doc-viewer-sidebar">
                    <div className="doc-viewer-sidebar-header">
                        <i className="fa fa-folder-open" />
                        <span>Sources</span>
                        <span className="doc-viewer-sidebar-count">{documents.length}</span>
                    </div>
                    {loading ? (
                        <div className="doc-viewer-sidebar-empty">
                            <div className="spinner" />
                            <p>Loading documents...</p>
                        </div>
                    ) : documents.length === 0 ? (
                        <div className="doc-viewer-sidebar-empty">
                            <i className="fa fa-inbox" />
                            <p>No documents for this patient.</p>
                        </div>
                    ) : (
                        grouped.map(({ category, items }) => (
                            <div key={category} className="doc-viewer-group">
                                <div className="doc-viewer-group-header">{category}</div>
                                {items.map((doc) => {
                                    const inPanel = panels.includes(doc.id);
                                    return (
                                        <div
                                            key={doc.id}
                                            className={`doc-viewer-source ${inPanel ? "in-panel" : ""}`}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, doc.id)}
                                            onClick={() => handleSidebarClick(doc.id)}
                                            title={doc.filename}
                                        >
                                            <i className={`fa ${iconForType(doc.document_type)}`} />
                                            <span className="doc-viewer-source-name">{doc.filename}</span>
                                            {inPanel && <i className="fa fa-eye doc-viewer-source-badge" />}
                                        </div>
                                    );
                                })}
                            </div>
                        ))
                    )}
                </aside>

                <main className={`doc-viewer-panels layout-${layout}`}>
                    {panels.map((docId, i) => {
                        const doc = docId ? docsById[docId] : null;
                        return (
                            <section
                                key={i}
                                className={`doc-viewer-panel ${dragOverPanel === i ? "drag-over" : ""}`}
                                onDragOver={(e) => handleDragOver(e, i)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, i)}
                            >
                                <header className="doc-viewer-panel-header">
                                    <i className={`fa ${doc ? iconForType(doc.document_type) : "fa-file-o"}`} />
                                    <span className="doc-viewer-panel-title">
                                        {doc ? doc.filename : `Panel ${i + 1}`}
                                    </span>
                                    {doc && (
                                        <div className="doc-viewer-panel-actions">
                                            {!isText(doc.document_type) && (
                                                <a
                                                    href={getMediaUrl(doc.filePath)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="doc-viewer-panel-btn"
                                                    title="Open in new tab"
                                                >
                                                    <i className="fa fa-external-link" />
                                                </a>
                                            )}
                                            <button
                                                type="button"
                                                className="doc-viewer-panel-btn"
                                                onClick={() => handlePanelClose(i)}
                                                title="Close"
                                            >
                                                <i className="fa fa-times" />
                                            </button>
                                        </div>
                                    )}
                                </header>
                                <div className="doc-viewer-panel-body">
                                    {!doc ? (
                                        <div className="doc-viewer-panel-empty">
                                            <i className="fa fa-arrow-circle-o-left" />
                                            <p>Drag a document from the sidebar</p>
                                        </div>
                                    ) : isPdf(doc.document_type) ? (
                                        <iframe
                                            src={getMediaUrl(doc.filePath)}
                                            title={doc.filename}
                                            className="doc-viewer-pdf"
                                        />
                                    ) : isText(doc.document_type) ? (
                                        <div className="doc-viewer-text markdown-content">
                                            <ReactMarkdown>{doc.content || ""}</ReactMarkdown>
                                        </div>
                                    ) : (
                                        <div className="doc-viewer-other">
                                            <i className="fa fa-file" />
                                            <p>{doc.filename}</p>
                                            <a
                                                href={getMediaUrl(doc.filePath)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-primary btn-sm"
                                            >
                                                Open file
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </section>
                        );
                    })}
                </main>
            </div>
        </div>
    );
};

export default DocumentViewerPage;
